import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Icon,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import moment from "moment";
import {
  IVoucherListItem,
  IVoucherTemplate,
  IChartOfAccount,
} from "../../../accounting.interface";
import { flattenAccounts } from "../../../utils";
import { useConfiguration } from "../../../components/ConfigurationContext";
import { useBranch } from "../../../components/BranchContext";

// Mock branches data until real API is integrated
const mockBranches = [
  { branch_id: "branch-1", name: "Main Branch" },
  { branch_id: "branch-2", name: "Secondary Branch" },
];

interface VoucherEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (voucher: Partial<IVoucherListItem>) => Promise<void>;
  isLoading?: boolean;
}

interface VoucherFormData {
  // Core Fields
  voucher_type: "payment" | "receipt" | "journal";
  template_id: string;
  voucher_date: string;
  total_amount: number | null;
  payment_method: "cash" | "bank_transfer" | "cheque" | "card" | "online" | "";
  branch_id: string;

  // Party & Reference Information
  beneficiary: string;
  reference_type: string;
  reference_id: string;
  reference_number: string;

  // Narration
  narration_template: string;
  template_variables: Record<string, string>;
  final_narration: string;

  // Attachments
  attachments: File[];
}

const steps = ["Select Template", "Enter Details", "Review & Submit"];

const createInitialFormData = (): VoucherFormData => ({
  voucher_type: "payment",
  template_id: "",
  voucher_date: moment().format("YYYY-MM-DD"),
  total_amount: null,
  payment_method: "",
  branch_id: "",
  beneficiary: "",
  reference_type: "",
  reference_id: "",
  reference_number: "",
  narration_template: "",
  template_variables: {},
  final_narration: "",
  attachments: [],
});

export const VoucherEntryDialog: React.FC<VoucherEntryDialogProps> = ({
  open,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { voucherSettings } = useConfiguration();
  const { selectedBranch } = useBranch();

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<VoucherFormData>(
    createInitialFormData()
  );
  const [selectedTemplate, setSelectedTemplate] =
    useState<IVoucherTemplate | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Memoized data
  const allAccounts = useMemo(() => {
    // TODO: Get accounts from COA hook when integrating voucher templates with API
    const accounts: IChartOfAccount[] = [];
    return flattenAccounts(accounts);
  }, []);

  const availableTemplates = useMemo(() => {
    // TODO: Get templates from voucher templates API when integrating
    const templates: IVoucherTemplate[] = [];
    return templates.filter(
      (template: IVoucherTemplate) =>
        template.status === "active" &&
        template.voucher_type === formData.voucher_type
    );
  }, [formData.voucher_type]);

  const enabledVoucherTypes = useMemo(() => {
    const types = [];
    if (voucherSettings.enabled_voucher_types.payment)
      types.push({ value: "payment", label: "Payment Voucher" });
    if (voucherSettings.enabled_voucher_types.receipt)
      types.push({ value: "receipt", label: "Receipt Voucher" });
    if (voucherSettings.enabled_voucher_types.journal)
      types.push({ value: "journal", label: "Journal Voucher" });
    return types;
  }, [voucherSettings.enabled_voucher_types]);

  // Template variables extraction
  const templateVariables = useMemo(() => {
    if (!selectedTemplate?.narration_template) return [];

    const regex = /\{\{(\w+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = regex.exec(selectedTemplate.narration_template)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }

    return variables;
  }, [selectedTemplate?.narration_template]);

  // Auto-generate final narration
  useEffect(() => {
    if (selectedTemplate?.narration_template) {
      let narration = selectedTemplate.narration_template;

      templateVariables.forEach((variable) => {
        const value =
          formData.template_variables[variable] || `{{${variable}}}`;
        narration = narration.replace(
          new RegExp(`\\{\\{${variable}\\}\\}`, "g"),
          value
        );
      });

      setFormData((prev) => ({ ...prev, final_narration: narration }));
    }
  }, [
    selectedTemplate?.narration_template,
    formData.template_variables,
    templateVariables,
  ]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...createInitialFormData(),
        branch_id: selectedBranch || "organization",
      }));
      setSelectedTemplate(null);
      setActiveStep(0);
      setErrors({});
    }
  }, [open, selectedBranch]);

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (templateId: string) => {
      const template = availableTemplates.find(
        (t: IVoucherTemplate) => t.template_id === templateId
      );
      if (template) {
        setSelectedTemplate(template);
        setFormData((prev) => ({
          ...prev,
          template_id: templateId,
          payment_method: template.default_payment_method as
            | "cash"
            | "bank_transfer"
            | "cheque"
            | "card"
            | "online"
            | "",
          narration_template: template.narration_template || "",
          reference_type: (template as any).reference_type || "",
        }));
      }
    },
    [availableTemplates]
  );

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof VoucherFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [errors]
  );

  // Handle template variable changes
  const handleTemplateVariableChange = useCallback(
    (variable: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        template_variables: {
          ...prev.template_variables,
          [variable]: value,
        },
      }));
    },
    []
  );

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (activeStep === 0 && !selectedTemplate) {
      setErrors({ template_id: "Please select a voucher template" });
      return;
    }

    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  }, [activeStep, selectedTemplate]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  // Validation
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedTemplate) {
      newErrors.template_id = "Template is required";
    }

    if (!formData.total_amount || formData.total_amount <= 0) {
      newErrors.total_amount = "Amount must be greater than 0";
    }

    if (
      selectedTemplate?.require_narration &&
      !formData.final_narration.trim()
    ) {
      newErrors.final_narration = "Narration is required";
    }

    if (
      selectedTemplate?.require_attachment &&
      formData.attachments.length === 0
    ) {
      newErrors.attachments = "At least one attachment is required";
    }

    // Check if all template variables are filled
    templateVariables.forEach((variable) => {
      if (!formData.template_variables[variable]?.trim()) {
        newErrors[`template_variable_${variable}`] = `${variable} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [selectedTemplate, formData, templateVariables]);

  // Save handlers
  const handleSaveAsDraft = useCallback(async () => {
    if (!validateForm()) return;

    const voucherData: Partial<IVoucherListItem> = {
      voucher_type: formData.voucher_type,
      voucher_date: moment(formData.voucher_date).valueOf(),
      narration: formData.final_narration,
      beneficiary: formData.beneficiary || undefined,
      linked_entity: formData.reference_id || undefined,
      payment_method: formData.payment_method
        ? (formData.payment_method as
            | "cash"
            | "bank_transfer"
            | "cheque"
            | "card"
            | "online")
        : undefined,
      reference_number: formData.reference_number || undefined,
      branch_id:
        formData.branch_id === "organization" ? undefined : formData.branch_id,
      status: "draft",
      total_amount: formData.total_amount!,
      // Additional fields would be populated here
    };

    await onSave(voucherData);
  }, [formData, validateForm, onSave]);

  const handleSubmitAndPost = useCallback(async () => {
    if (!validateForm()) return;

    const voucherData: Partial<IVoucherListItem> = {
      voucher_type: formData.voucher_type,
      voucher_date: moment(formData.voucher_date).valueOf(),
      narration: formData.final_narration,
      beneficiary: formData.beneficiary || undefined,
      linked_entity: formData.reference_id || undefined,
      payment_method: formData.payment_method
        ? (formData.payment_method as
            | "cash"
            | "bank_transfer"
            | "cheque"
            | "card"
            | "online")
        : undefined,
      reference_number: formData.reference_number || undefined,
      branch_id:
        formData.branch_id === "organization" ? undefined : formData.branch_id,
      status: "posted",
      total_amount: formData.total_amount!,
      posted_at: moment().valueOf(),
      // Additional fields would be populated here
    };

    await onSave(voucherData);
  }, [formData, validateForm, onSave]);

  const handleClose = useCallback(() => {
    setFormData(createInitialFormData());
    setSelectedTemplate(null);
    setActiveStep(0);
    setErrors({});
    onClose();
  }, [onClose]);

  // Get account name helper
  const getAccountName = useCallback(
    (accountId: string) => {
      const account = allAccounts.find((acc) => acc.id === accountId);
      return account
        ? `${account.accountCode} - ${account.accountName}`
        : "Unknown Account";
    },
    [allAccounts]
  );

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 3 }}>
            {/* Voucher Type Selection */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Select Voucher Type
                </Typography>
                <Grid container spacing={2}>
                  {enabledVoucherTypes.map((type) => (
                    <Grid item xs={12} sm={4} key={type.value}>
                      <Card
                        sx={{
                          cursor: "pointer",
                          border:
                            formData.voucher_type === type.value
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${theme.palette.divider}`,
                          bgcolor:
                            formData.voucher_type === type.value
                              ? alpha(theme.palette.primary.main, 0.05)
                              : "background.paper",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                        onClick={() =>
                          handleFieldChange("voucher_type", type.value)
                        }
                      >
                        <CardContent sx={{ textAlign: "center", py: 2 }}>
                          <Icon
                            sx={{
                              fontSize: 32,
                              mb: 1,
                              color:
                                formData.voucher_type === type.value
                                  ? "primary.main"
                                  : "text.secondary",
                            }}
                          >
                            {type.value === "payment"
                              ? "payment"
                              : type.value === "receipt"
                              ? "receipt"
                              : "book"}
                          </Icon>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            {type.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Template Selection */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Select Voucher Template
                </Typography>

                {availableTemplates.length === 0 ? (
                  <Alert severity="warning">
                    No active templates available for {formData.voucher_type}{" "}
                    vouchers. Please create templates in the configuration
                    section.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {availableTemplates.map((template) => (
                      <Grid item xs={12} sm={6} key={template.template_id}>
                        <Card
                          sx={{
                            cursor: "pointer",
                            border:
                              selectedTemplate?.template_id ===
                              template.template_id
                                ? `2px solid ${theme.palette.primary.main}`
                                : `1px solid ${theme.palette.divider}`,
                            bgcolor:
                              selectedTemplate?.template_id ===
                              template.template_id
                                ? alpha(theme.palette.primary.main, 0.05)
                                : "background.paper",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                            },
                          }}
                          onClick={() =>
                            handleTemplateSelect(template.template_id)
                          }
                        >
                          <CardContent>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600, mb: 1 }}
                            >
                              {template.template_name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", mb: 2 }}
                            >
                              {template.transaction_code}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                mb: 2,
                                flexWrap: "wrap",
                              }}
                            >
                              {template.allowed_payment_methods?.map(
                                (method) => (
                                  <Chip
                                    key={method}
                                    label={method}
                                    size="small"
                                    variant="outlined"
                                  />
                                )
                              )}
                            </Box>

                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {template.debit_account_id
                                ? getAccountName(template.debit_account_id)
                                : "No debit account"}{" "}
                              →{" "}
                              {template.credit_account_id
                                ? getAccountName(template.credit_account_id)
                                : "No credit account"}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {errors.template_id && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errors.template_id}
                  </FormHelperText>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 3 }}>
            <Grid container spacing={3}>
              {/* Core Fields */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Core Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Voucher Date"
                          type="date"
                          value={formData.voucher_date}
                          onChange={(e) =>
                            handleFieldChange("voucher_date", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={formData.total_amount || ""}
                          onChange={(e) =>
                            handleFieldChange(
                              "total_amount",
                              parseFloat(e.target.value) || null
                            )
                          }
                          error={!!errors.total_amount}
                          helperText={errors.total_amount}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                BHD
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {selectedTemplate?.allowed_payment_methods &&
                        selectedTemplate.allowed_payment_methods.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Payment Method</InputLabel>
                              <Select
                                value={formData.payment_method}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "payment_method",
                                    e.target.value
                                  )
                                }
                                label="Payment Method"
                              >
                                {selectedTemplate.allowed_payment_methods.map(
                                  (method) => (
                                    <MenuItem key={method} value={method}>
                                      {method}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Branch</InputLabel>
                          <Select
                            value={formData.branch_id}
                            onChange={(e) =>
                              handleFieldChange("branch_id", e.target.value)
                            }
                            label="Branch"
                          >
                            <MenuItem value="organization">
                              Organization Level
                            </MenuItem>
                            {mockBranches.map((branch) => (
                              <MenuItem
                                key={branch.branch_id}
                                value={branch.branch_id}
                              >
                                {branch.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Party & Reference Information */}
              {(formData.voucher_type === "payment" ||
                formData.voucher_type === "receipt") && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Party & Reference Information
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={
                              formData.voucher_type === "payment"
                                ? "Vendor/Payee"
                                : "Customer/Payer"
                            }
                            value={formData.beneficiary}
                            onChange={(e) =>
                              handleFieldChange("beneficiary", e.target.value)
                            }
                          />
                        </Grid>

                        {(selectedTemplate as any)
                          ?.enable_reference_linking && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Reference ID"
                                value={formData.reference_id}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "reference_id",
                                    e.target.value
                                  )
                                }
                                placeholder={`e.g., ${
                                  (selectedTemplate as any)?.reference_type ||
                                  "Reference"
                                } number`}
                              />
                            </Grid>
                          </>
                        )}

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Reference Number"
                            value={formData.reference_number}
                            onChange={(e) =>
                              handleFieldChange(
                                "reference_number",
                                e.target.value
                              )
                            }
                            placeholder="Cheque number, transfer reference, etc."
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Narration Section */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Narration
                    </Typography>

                    {templateVariables.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 2, color: "text.secondary" }}
                        >
                          Fill in the template variables:
                        </Typography>
                        <Grid container spacing={2}>
                          {templateVariables.map((variable) => (
                            <Grid item xs={12} sm={6} key={variable}>
                              <TextField
                                fullWidth
                                label={
                                  variable.charAt(0).toUpperCase() +
                                  variable.slice(1)
                                }
                                value={
                                  formData.template_variables[variable] || ""
                                }
                                onChange={(e) =>
                                  handleTemplateVariableChange(
                                    variable,
                                    e.target.value
                                  )
                                }
                                error={
                                  !!errors[`template_variable_${variable}`]
                                }
                                helperText={
                                  errors[`template_variable_${variable}`]
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    <TextField
                      fullWidth
                      label="Final Narration"
                      multiline
                      rows={3}
                      value={formData.final_narration}
                      onChange={(e) =>
                        handleFieldChange("final_narration", e.target.value)
                      }
                      disabled={
                        selectedTemplate
                          ? !selectedTemplate.allow_editing_narration
                          : false
                      }
                      error={!!errors.final_narration}
                      helperText={
                        errors.final_narration ||
                        (selectedTemplate?.allow_editing_narration
                          ? "You can edit this narration"
                          : "Narration is auto-generated from template")
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* COA Line Summary */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Account Mapping
                    </Typography>

                    {selectedTemplate && (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ color: "text.secondary", mb: 1 }}
                              >
                                Debit Account
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedTemplate.debit_account_id
                                  ? getAccountName(
                                      selectedTemplate.debit_account_id
                                    )
                                  : "No debit account"}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ color: "text.secondary", mb: 1 }}
                              >
                                Credit Account
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedTemplate.credit_account_id
                                  ? getAccountName(
                                      selectedTemplate.credit_account_id
                                    )
                                  : "No credit account"}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Review & Confirm
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <Icon>description</Icon>
                        </ListItemIcon>
                        <ListItemText
                          primary="Voucher Type"
                          secondary={formData.voucher_type}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Icon>template</Icon>
                        </ListItemIcon>
                        <ListItemText
                          primary="Template"
                          secondary={selectedTemplate?.template_name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Icon>calendar_today</Icon>
                        </ListItemIcon>
                        <ListItemText
                          primary="Date"
                          secondary={moment(formData.voucher_date).format(
                            "DD MMM YYYY"
                          )}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Icon>attach_money</Icon>
                        </ListItemIcon>
                        <ListItemText
                          primary="Amount"
                          secondary={`BHD ${formData.total_amount?.toLocaleString()}`}
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <List dense>
                      {formData.payment_method && (
                        <ListItem>
                          <ListItemIcon>
                            <Icon>payment</Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary="Payment Method"
                            secondary={formData.payment_method}
                          />
                        </ListItem>
                      )}
                      {formData.beneficiary && (
                        <ListItem>
                          <ListItemIcon>
                            <Icon>person</Icon>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              formData.voucher_type === "payment"
                                ? "Payee"
                                : "Payer"
                            }
                            secondary={formData.beneficiary}
                          />
                        </ListItem>
                      )}
                      <ListItem>
                        <ListItemIcon>
                          <Icon>business</Icon>
                        </ListItemIcon>
                        <ListItemText
                          primary="Branch"
                          secondary={
                            formData.branch_id === "organization"
                              ? "Organization Level"
                              : mockBranches.find(
                                  (b) => b.branch_id === formData.branch_id
                                )?.name
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Narration
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="body2">
                        {formData.final_narration}
                      </Typography>
                    </Paper>
                  </Grid>

                  {selectedTemplate && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Journal Entry Preview
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, color: "success.main" }}
                            >
                              Debit:{" "}
                              {selectedTemplate.debit_account_id
                                ? getAccountName(
                                    selectedTemplate.debit_account_id
                                  )
                                : "No debit account"}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ color: "success.main" }}
                            >
                              BHD {formData.total_amount?.toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, color: "error.main" }}
                            >
                              Credit:{" "}
                              {selectedTemplate.credit_account_id
                                ? getAccountName(
                                    selectedTemplate.credit_account_id
                                  )
                                : "No credit account"}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ color: "error.main" }}
                            >
                              BHD {formData.total_amount?.toLocaleString()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Create New Voucher
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Follow the steps to create a new voucher entry
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: "error.main",
              },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Stepper */}
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        <Box sx={{ px: 3, minHeight: 400 }}>{renderStepContent()}</Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>

        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isLoading}>
            Back
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isLoading || (activeStep === 0 && !selectedTemplate)}
          >
            Next
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleSaveAsDraft}
              disabled={isLoading}
              startIcon={<Icon>save</Icon>}
            >
              Save as Draft
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitAndPost}
              disabled={isLoading}
              startIcon={<Icon>check_circle</Icon>}
            >
              Submit & Post
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};
